import interstellarApi from './interstellarApi';

/** Tag dentro de una estrategia de resolución (con jerarquía padre/hijo). */
export interface ResolutionStrategyTag {
  id: number;
  call_resolution_strategy_id: number;
  parent_id: number | null;
  name: string;
  /** Orden entre hermanos. Default 0 para filas legacy; el back-end tie-breaks por id. */
  sort_order?: number;
  created_at?: string | null;
  modified_at?: string | null;
}

export interface ResolutionStrategy {
  id: number;
  account_id: number;
  name: string;
  resolution_time: number;
  tags: ResolutionStrategyTag[];
  created_at?: string | null;
  modified_at?: string | null;
}

interface ResolutionStrategiesListResponse {
  total_count: number;
  page: number;
  page_size: number;
  resolution_strategies: ResolutionStrategy[];
}

export const fetchResolutionStrategies = async (
  accountId: number,
  pageSize = 50
): Promise<ResolutionStrategy[]> => {
  const response = await interstellarApi.get<ResolutionStrategiesListResponse>(
    '/incoming-call-config/resolution-strategy/',
    {
      params: {
        account_id: accountId,
        page: 1,
        page_size: pageSize,
      },
    }
  );
  return response.data.resolution_strategies ?? [];
};

export const fetchResolutionStrategy = async (strategyId: number): Promise<ResolutionStrategy> => {
  const response = await interstellarApi.get<ResolutionStrategy>(
    `/incoming-call-config/resolution-strategy/${strategyId}`
  );
  return response.data;
};

/** Tag input for creating/updating a resolution strategy */
export interface ResolutionStrategyTagInput {
  name: string;
  /** Reference to an existing tag (for updates) */
  id?: number;
  /** Client-side temp ID for wiring parent/child in same request */
  temp_id?: string;
  /** Reference another tag by database ID */
  parent_id?: number;
  /** Reference another tag by temp_id in same payload */
  parent_temp_id?: string;
}

export interface CreateResolutionStrategyPayload {
  name: string;
  /** Tags as objects with name and optional hierarchy */
  tags: ResolutionStrategyTagInput[];
  /** Time in seconds the agent has to classify the call */
  resolution_time: number;
}

interface CreateResolutionStrategyResponse {
  message: string;
  id: number;
}

export const createResolutionStrategy = async (
  accountId: number,
  payload: CreateResolutionStrategyPayload
): Promise<CreateResolutionStrategyResponse> => {
  const response = await interstellarApi.post<CreateResolutionStrategyResponse>(
    '/incoming-call-config/resolution-strategy',
    payload,
    { params: { account_id: accountId } }
  );
  return response.data;
};

export interface CreateTagPayload {
  strategy_id: number;
  name: string;
  parent_id?: number | null;
}

export const createResolutionStrategyTag = async (
  payload: CreateTagPayload
): Promise<ResolutionStrategyTag> => {
  const response = await interstellarApi.post<ResolutionStrategyTag>(
    '/incoming-call-config/resolution-strategy-tag',
    {
      strategy_id: payload.strategy_id,
      name: payload.name,
      parent_id: payload.parent_id ?? null,
    }
  );
  return response.data;
};

export interface UpdateTagPayload {
  name?: string;
  parent_id?: number | null;
  /** When true, send `clear_parent: true` to the server (mutually exclusive with `parent_id`). */
  clear_parent?: boolean;
}

export const updateResolutionStrategyTag = async (
  tagId: number,
  payload: UpdateTagPayload
): Promise<ResolutionStrategyTag> => {
  const body: Record<string, unknown> = {};
  if (payload.name !== undefined) body.name = payload.name;
  if (payload.clear_parent) {
    body.clear_parent = true;
  } else if (payload.parent_id !== undefined && payload.parent_id !== null) {
    body.parent_id = payload.parent_id;
  }
  const response = await interstellarApi.put<ResolutionStrategyTag>(
    `/incoming-call-config/resolution-strategy-tag/${tagId}`,
    body
  );
  return response.data;
};

export const deleteResolutionStrategyTag = async (tagId: number): Promise<void> => {
  await interstellarApi.delete(`/incoming-call-config/resolution-strategy-tag/${tagId}`);
};

/** Item del payload de reordenamiento (drag-and-drop). */
export interface ReorderTagItem {
  id: number;
  parent_id: number | null;
  sort_order: number;
}

interface ReorderTagsResponse {
  strategy_id: number;
  tags: ResolutionStrategyTag[];
}

/**
 * Actualiza `parent_id` y `sort_order` de varias tags de una estrategia
 * en un solo request. Incluye detección de ciclos en el servidor.
 */
export const reorderResolutionStrategyTags = async (payload: {
  strategy_id: number;
  items: ReorderTagItem[];
}): Promise<ResolutionStrategyTag[]> => {
  const response = await interstellarApi.post<ReorderTagsResponse>(
    '/incoming-call-config/resolution-strategy-tag/reorder',
    payload
  );
  return response.data.tags;
};
